import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Lottie from 'lottie-react';
import { Copy, CheckCircle2, Sparkles, Trophy, Crown, Ticket, Settings, Download, Check } from 'lucide-react';
import AnimatedText from '../components/AnimatedText';
import starTrophyUnlockData from '../assets/lottie/star_trophy_unlock.json';

const CodesPage: React.FC = () => {
  const { lang } = useParams<{ lang?: string }>();
  const [searchParams] = useSearchParams();
  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || 'en';
  const [copiedCode, setCopiedCode] = useState<boolean>(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'both'>('both');

  // Get parameters from URL
  const code = searchParams.get('code') || 'TTSTATS-XXXXXXXX';
  const tournament = searchParams.get('tournament') || 'Tournament';
  const duration = searchParams.get('duration') || '12';

  // Detect platform
  React.useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    const isAndroid = /android/i.test(userAgent);

    if (isIOS) {
      setPlatform('ios');
    } else if (isAndroid) {
      setPlatform('android');
    }
  }, []);

  const translations = {
    en: {
      congratulations: 'Congratulations!',
      yourPrize: 'Your Prize',
      pro: 'TTStats Pro',
      subtitle: 'You\'ve earned something special',
      description: 'Unlock all premium features',
      redeemInstructions: 'Redeem in 3 easy steps',
      step1: 'Download or open TTStats',
      step2: 'Tap Settings → Redeem Code',
      step3: 'Paste your code & unlock Pro',
      yourCode: 'Your Prize Code',
      copyCode: 'Copy Code',
      copied: 'Copied!',
      months: 'months',
      inPartnership: 'In partnership with',
      proFeatures: 'Everything included in Pro',
      getApp: 'Get the TTStats App',
      downloadFree: 'Download for free',
      features: [
        'Find Tournaments',
        'Advanced Player Analysis',
        'Unlimited Upcoming Matches',
        'Unlimited Match Calculator uses',
        'Unlimited Favorite Players',
        'Unlimited Recent Searches',
        'TTStats AI Analysis (Beta)',
        'Add Future Matches to Calendar',
        'Tournament Analysis',
        'Club Player Lists',
        'Detailed Performance Stats',
        'Performance by opponent strength',
        'Performance by day of week',
        'Monthly performance graphs',
        'Competition Analysis',
        'Win rates by competition',
        'Performance trends over time',
        'Individual tournament insights'
      ]
    },
    de: {
      congratulations: 'Gratulation!',
      yourPrize: 'Dein Gewinn',
      pro: 'TTStats Pro',
      subtitle: 'Du hast dir etwas Besonderes verdient',
      description: 'Schalte alle Premium-Funktionen frei',
      redeemInstructions: 'In 3 einfachen Schritten einlösen',
      step1: 'TTStats downloaden oder öffnen',
      step2: 'Einstellungen → Code einlösen',
      step3: 'Code einfügen & Pro freischalten',
      yourCode: 'Dein Gewinn-Code',
      copyCode: 'Code kopieren',
      copied: 'Kopiert!',
      months: 'Monate',
      inPartnership: 'In Partnerschaft mit',
      proFeatures: 'Alles in Pro enthalten',
      getApp: 'Hol dir TTStats',
      downloadFree: 'Kostenlos downloaden',
      features: [
        'Turniere finden',
        'Erweiterte Spieleranalyse',
        'Unbegrenzte bevorstehende Spiele',
        'Unbegrenzte Match-Rechner Nutzung',
        'Unbegrenzte Lieblingsspieler',
        'Unbegrenzte letzte Suchen',
        'TTStats KI-Analyse (Beta)',
        'Zukünftige Spiele zum Kalender hinzufügen',
        'Turnieranalyse',
        'Vereinsspielerlisten',
        'Detaillierte Leistungsstatistiken',
        'Leistung nach Gegnerstärke',
        'Leistung nach Wochentag',
        'Monatliche Leistungsgraphen',
        'Wettbewerbsanalyse',
        'Gewinnraten nach Wettbewerb',
        'Leistungstrends im Zeitverlauf',
        'Individuelle Turniereinblicke'
      ]
    },
    fr: {
      congratulations: 'Félicitations !',
      yourPrize: 'Ton prix',
      pro: 'TTStats Pro',
      subtitle: 'Tu as mérité quelque chose de spécial',
      description: 'Débloque toutes les fonctionnalités premium',
      redeemInstructions: 'Active en 3 étapes faciles',
      step1: 'Télécharge ou ouvre TTStats',
      step2: 'Paramètres → Activer le code',
      step3: 'Colle ton code & débloque Pro',
      yourCode: 'Ton code gagnant',
      copyCode: 'Copier le code',
      copied: 'Copié !',
      months: 'mois',
      inPartnership: 'En partenariat avec',
      proFeatures: 'Tout ce qui est inclus dans Pro',
      getApp: 'Télécharge TTStats',
      downloadFree: 'Téléchargement gratuit',
      features: [
        'Trouver des tournois',
        'Analyse avancée des joueurs',
        'Matchs à venir illimités',
        'Utilisations illimitées du calculateur',
        'Joueurs favoris illimités',
        'Recherches récentes illimitées',
        'Analyse IA TTStats (Beta)',
        'Ajouter les matchs au calendrier',
        'Analyse de tournoi',
        'Listes de joueurs de club',
        'Statistiques de performance détaillées',
        'Performance selon la force adverse',
        'Performance par jour de la semaine',
        'Graphiques de performance mensuels',
        'Analyse de compétition',
        'Taux de victoire par compétition',
        'Tendances de performance',
        'Insights individuels de tournoi'
      ]
    },
    it: {
      congratulations: 'Congratulazioni!',
      yourPrize: 'Il tuo premio',
      pro: 'TTStats Pro',
      subtitle: 'Ti sei meritato qualcosa di speciale',
      description: 'Sblocca tutte le funzionalità premium',
      redeemInstructions: 'Riscatta in 3 semplici passi',
      step1: 'Scarica o apri TTStats',
      step2: 'Impostazioni → Riscatta codice',
      step3: 'Incolla il codice & sblocca Pro',
      yourCode: 'Il tuo codice premio',
      copyCode: 'Copia Codice',
      copied: 'Copiato!',
      months: 'mesi',
      inPartnership: 'In collaborazione con',
      proFeatures: 'Tutto incluso in Pro',
      getApp: 'Scarica TTStats',
      downloadFree: 'Download gratuito',
      features: [
        'Trova tornei',
        'Analisi avanzata giocatori',
        'Partite imminenti illimitate',
        'Utilizzi illimitati del calcolatore',
        'Giocatori preferiti illimitati',
        'Ricerche recenti illimitate',
        'Analisi AI TTStats (Beta)',
        'Aggiungi partite al calendario',
        'Analisi torneo',
        'Liste giocatori club',
        'Statistiche prestazioni dettagliate',
        'Prestazioni per forza avversario',
        'Prestazioni per giorno della settimana',
        'Grafici prestazioni mensili',
        'Analisi competizione',
        'Tassi di vittoria per competizione',
        'Tendenze prestazioni nel tempo',
        'Approfondimenti tornei individuali'
      ]
    }
  };

  const t = translations[currentLang] || translations.en;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const appStoreUrl = 'https://apps.apple.com/ch/app/ttstats-swiss-table-tennis/id6744279609';
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.peaktwilight.ttstats';

  const getAppUrl = () => {
    if (platform === 'ios') return appStoreUrl;
    if (platform === 'android') return playStoreUrl;
    return appStoreUrl; // default to iOS if both
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 relative overflow-hidden transition-colors duration-300">
      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12 relative z-10">

        {/* Lottie Animation - centered and elegant */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.6,
            type: "spring",
            bounce: 0.3,
            delay: 0.2
          }}
          className="flex justify-center -mb-8 md:-mb-12"
        >
          <div className="relative w-64 h-64 md:w-80 md:h-80">
            <Lottie
              animationData={starTrophyUnlockData}
              loop={false}
              autoplay={true}
              style={{ width: '100%', height: '100%' }}
              rendererSettings={{
                preserveAspectRatio: 'xMidYMid meet'
              }}
            />
          </div>
        </motion.div>

        {/* Header - celebration moment */}
        <div className="text-center mb-8">
          <AnimatedText
            text={t.congratulations}
            className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-3 whitespace-nowrap transition-colors duration-300"
            delay={0.3}
            staggerDelay={0.03}
          />

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mb-6 transition-colors duration-300"
          >
            {t.subtitle}
          </motion.p>

          {/* Prize info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="flex flex-col items-center gap-3 mb-6"
          >
            <div className="text-center">
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2 transition-colors duration-300">{t.yourPrize}</div>
              <motion.div
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-bold text-2xl shadow-lg bg-[#ff9f10]"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Crown className="w-6 h-6" />
                <span>{duration} {t.months} {t.pro}</span>
              </motion.div>
            </div>
          </motion.div>

          <motion.div
            className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <Trophy className="w-4 h-4 text-[#ff9f10]" />
            <span>{t.inPartnership} {tournament}</span>
          </motion.div>
        </div>

        {/* Code Card - sleek glass morphism design */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mb-12"
        >
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden transition-colors duration-300"
          >
            {/* Header */}
            <div className="bg-[#1C7AD6] px-6 py-5">
              <h3 className="text-white font-semibold text-lg text-center flex items-center justify-center gap-2">
                <Ticket className="w-5 h-5" />
                {t.yourCode}
              </h3>
            </div>

            {/* Code Display */}
            <div className="p-8 md:p-10">
              <motion.div
                className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-6 mb-6 border-2 border-gray-200 dark:border-gray-600 cursor-pointer transition-colors duration-300"
                onClick={handleCopyCode}
                whileHover={{ scale: 1.02, borderColor: '#1C7AD6' }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <p className="text-2xl md:text-4xl font-mono font-bold text-[#ff9f10] text-center tracking-wider select-all">
                  {code}
                </p>
              </motion.div>

              {/* Copy Button */}
              <AnimatePresence mode="wait">
                <motion.button
                  key={copiedCode ? 'copied' : 'copy'}
                  onClick={handleCopyCode}
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 shadow-md ${
                    copiedCode
                      ? 'bg-green-500 text-white'
                      : 'bg-[#1C7AD6] hover:bg-[#1565C0] text-white'
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  {copiedCode ? (
                    <>
                      <CheckCircle2 className="w-6 h-6" />
                      {t.copied}
                    </>
                  ) : (
                    <>
                      <Copy className="w-6 h-6" />
                      {t.copyCode}
                    </>
                  )}
                </motion.button>
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>

        {/* Instructions - mobile-first design */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="mb-8"
        >
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center transition-colors duration-300">
            {t.redeemInstructions}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 relative z-10">
            {[
              { text: t.step1, icon: Download, num: 1, clickable: true },
              { text: t.step2, icon: Settings, num: 2, clickable: false },
              { text: t.step3, icon: Check, num: 3, clickable: false }
            ].map((step, index) => {
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 1 + index * 0.15 }}
                  {...(step.clickable ? {
                    as: 'a',
                    href: getAppUrl(),
                    target: '_blank',
                    rel: 'noopener noreferrer'
                  } : {})}
                  className={`flex flex-col items-center text-center p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-[#1C7AD6] hover:shadow-lg transition-all duration-300 ${step.clickable ? 'cursor-pointer' : ''}`}
                >
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1C7AD6] to-blue-500 text-white flex items-center justify-center text-2xl font-bold shadow-lg mb-4">
                    {step.num}
                  </div>
                  <step.icon className="w-8 h-8 text-[#1C7AD6] mb-3" />
                  <p className="text-gray-700 dark:text-gray-300 text-base font-medium leading-relaxed transition-colors duration-300">{step.text}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Pro Features - compact, scannable */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.2 }}
          className="mb-12"
        >
          <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center flex items-center justify-center gap-2 transition-colors duration-300">
            <Sparkles className="w-7 h-7 text-[#ff9f10]" />
            {t.proFeatures}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {t.features.slice(0, 8).map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.3 + idx * 0.03 }}
                className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-[#1C7AD6] hover:shadow-sm transition-all duration-300"
              >
                <Check className="w-5 h-5 text-[#ff9f10] flex-shrink-0" />
                <span className="text-sm md:text-base text-gray-700 dark:text-gray-300 transition-colors duration-300">{feature}</span>
              </motion.div>
            ))}
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6 }}
            className="text-center text-gray-500 dark:text-gray-400 mt-4 text-sm transition-colors duration-300"
          >
            + {t.features.length - 8} more features
          </motion.p>
        </motion.div>

        {/* Get App CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="bg-gradient-to-br from-[#1C7AD6] to-blue-600 rounded-3xl p-8 md:p-10 text-center text-white shadow-2xl"
        >
          <Download className="w-12 h-12 mx-auto mb-4" />
          <h3 className="text-2xl md:text-3xl font-bold mb-3">{t.getApp}</h3>
          <p className="text-blue-100 mb-6 text-lg">{t.downloadFree}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {(platform === 'ios' || platform === 'both') && (
              <a
                href={appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#1C7AD6] rounded-xl font-semibold hover:bg-gray-100 transition-all shadow-lg"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5M13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                </svg>
                App Store
              </a>
            )}
            {(platform === 'android' || platform === 'both') && (
              <a
                href={playStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#1C7AD6] rounded-xl font-semibold hover:bg-gray-100 transition-all shadow-lg"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                Google Play
              </a>
            )}
          </div>
        </motion.div>

        {/* Bottom spacing */}
        <div className="h-12" />
      </div>
    </div>
  );
};

export default CodesPage;
